import React from 'react';
import { useDeleteWebhookMutation, useGetWebhooksQuery } from '../../store/endpoints/WebhooksEndpoints';
import { IWebhook } from '../../models/Webhooks';
import WebhooksTable from '../../components/Webhooks/WebhooksTable';
import WebhookPanel from '../../components/Webhooks/WebhookPanel';
import { CommandBar, DefaultButton, Dialog, DialogFooter, ICommandBarItemProps } from '@fluentui/react';
import { useUrlPanelState } from '../../hooks/useUrlState';

const WebhooksPage: React.FC = () => {
    const [page, setPage] = React.useState(1);
    const webhooks = useGetWebhooksQuery({page});
    // Creating a webhook is a transient flow with nothing to link to, so it stays local; editing an
    // existing one goes through the URL so a link can open it.
    const [creating, setCreating] = React.useState(false);
    const webhookPanel = useUrlPanelState('webhook');
    const [pendingDelete, setPendingDelete] = React.useState<IWebhook | undefined>();
    const [deleteWebhook] = useDeleteWebhookMutation();

    // Resolved from the URL so a link opens the right webhook once the list has arrived.
    const edit = creating
        ? undefined
        : webhooks.data?.items?.find((w) => w.id === webhookPanel.instanceId);

    const open = (wh: IWebhook) => {
        setCreating(false);
        webhookPanel.open(wh.id);
    }

    const closePanel = () => {
        setCreating(false);
        webhookPanel.close();
    }

    const commands: ICommandBarItemProps[] = [
        {
            key: 'Add',
            text: 'Add Webhook',
            iconProps: { iconName: 'Add' },
            onClick: () => {
                setCreating(true);
                webhookPanel.open();
            }
        }
    ]

    const farComands: ICommandBarItemProps[] = [
        {
            key: 'Refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            iconOnly: true,
            onClick: () => {webhooks.refetch();}
        }
    ]

    const deleteWebhookHandler = () => {
        if (pendingDelete) {
            deleteWebhook({id: pendingDelete.id || ''});
            setPendingDelete(undefined);
        }
    }

    return (
        <div>
            <h1>Webhooks</h1>
            <CommandBar items={commands} farItems={farComands} />
            <WebhooksTable
                webhooks={webhooks.data?.items}
                isLoading={webhooks.isLoading}
                onEdit={open}
                onDelete={setPendingDelete}
            />
            <WebhookPanel
                webhook={edit}
                isOpen={webhookPanel.isOpen && (creating || !!edit)}
                onDismiss={closePanel}
                initialTab={creating ? undefined : webhookPanel.panelTab}
            />
            <Dialog
                hidden={!pendingDelete}
                onDismiss={() => setPendingDelete(undefined)}
                >
                    <h4>Delete Webhook</h4>
                    <p>Are you sure you want to delete the webhook '{pendingDelete?.name}'?</p>
                    <DialogFooter>
                        <DefaultButton className='btn-warning' onClick={deleteWebhookHandler}>Delete</DefaultButton>
                        <DefaultButton onClick={() => setPendingDelete(undefined)}>Cancel</DefaultButton>
                    </DialogFooter>
            </Dialog>
        </div>
    );
};

export default WebhooksPage;